import React, { ReactNode } from "react";
import { Box, Divider, SxProps, Theme } from "@mui/material";
import { CompareSection, DataCell, Label, RightContent } from "../DataCell";
import {
  COLOR_CONSTANTS,
  SPACING_CONSTANTS,
} from "../../constants/styles/index";

interface ComparisonSectionProps {
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
  firstNumber?: number;
  secondNumber?: number;
  items?: Array<{
    label: string;
    value: string | ReactNode;
    unit: string;
    valueColor?: string;
    fontWeight?: number;
    textAlign?: "left" | "center" | "right";
    /**
     * 값이 잘렸을 때 hover 팝업으로 보여줄 본문.
     * `value` 가 문자열이면 생략해도 그 값이 쓰인다 — 노드일 때만 필요하다.
     */
    valueTooltipText?: string;
    /**
     * 라벨이 잘렸을 때 hover 팝업으로 보여줄 본문.
     * `label` 자신이 기본 본문이므로, 라벨과 다른 내용을 띄울 때만 준다.
     */
    labelTooltipText?: string;
  }>;
}

export const ComparisonSection: React.FC<ComparisonSectionProps> = ({
  children,
  sx,
  firstNumber = 1,
  secondNumber = 2,
  items = [],
}) => {
  return (
    <Box sx={{ ...sx }}>
      <Box sx={{ height: `${SPACING_CONSTANTS.spacing[4]}px` }} />
      <Divider sx={{ borderColor: COLOR_CONSTANTS.COLOR.GREY[300] }} />

      {/* 첫 번째 행: 왼쪽(비교 라벨) + 중간(비교) + 오른쪽(빈 공간) */}
      <DataCell
        sx={{
          minHeight: "30px",
          backgroundColor: "#ffffff",
          padding: `0 ${SPACING_CONSTANTS.spacing[2]}px`,
          marginTop: `${SPACING_CONSTANTS.spacing[5]}px`,
        }}
      >
        <DataCell.Left>
          <CompareSection
            firstNumber={firstNumber}
            secondNumber={secondNumber}
            label="비교"
          />
        </DataCell.Left>
        <DataCell.Center>
          <Label tooltipText={items[0].labelTooltipText}>
            {items[0].label}
          </Label>
        </DataCell.Center>
        <DataCell.Right>
          <RightContent
            mainText={items[0].value}
            subText={items[0].unit}
            valueColor={items[0].valueColor}
            fontWeight={items[0].fontWeight}
            textAlign={items[0].textAlign}
            tooltipText={items[0].valueTooltipText}
          />
        </DataCell.Right>
      </DataCell>

      {/* 나머지 아이템들을 동일한 구조로 처리 (1번부터) */}
      {items.slice(1).map((item, index) => (
        <DataCell
          key={index}
          sx={{
            minHeight: "30px",
            padding: `0 ${SPACING_CONSTANTS.spacing[2]}px`,
          }}
        >
          <DataCell.Left>
            {/* 빈 공간 - 첫 번째 행의 Left와 동일한 너비 유지 */}
          </DataCell.Left>
          <DataCell.Center>
            <Label tooltipText={item.labelTooltipText}>{item.label}</Label>
          </DataCell.Center>
          <DataCell.Right>
            <RightContent
              mainText={item.value}
              subText={item.unit}
              valueColor={item.valueColor}
              fontWeight={item.fontWeight}
              textAlign={item.textAlign}
              tooltipText={item.valueTooltipText}
            />
          </DataCell.Right>
        </DataCell>
      ))}
      {children}
    </Box>
  );
};

export type { ComparisonSectionProps };
